import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { createProvisionalConversationAgent } from '@atlassian/conversation-assistant-store/controllers/chat/conversation-agent-identity';
import { StoreActionLoggableName } from '@atlassian/conversation-assistant-store/controllers/chat/types';

import { createConversationOptimistically } from './createConversationOptimistically';
import { createSafeAction, type SafeAction } from './createSafeAction';
import { fetchConversation } from './fetchConversation';
import { setConversationData } from './setConversationData';
import { setSelectedConversation } from './setSelectedConversation';
import type { ChatStoreAction } from './types';

type HandleChatOpenPubsubActionArgs = { conversationId: string; agentId?: string };

export const handleChatOpenPubsubAction: SafeAction<[HandleChatOpenPubsubActionArgs]> =
	createSafeAction(
		StoreActionLoggableName.handleChatOpenPubsubAction,
		({ conversationId, agentId }: HandleChatOpenPubsubActionArgs): ChatStoreAction =>
			async ({ getState, dispatch }) => {
				// IF convo id is the current chat
				// refetch the chat, do not create new one
				if (conversationId === getState().currentConversationId) {
					const shouldStoreChatNewAgentId =
						!!agentId && isExperimentEnabled('rovo_chat_mention_agents');
					if (shouldStoreChatNewAgentId) {
						const conversationAgentFromChatNew = createProvisionalConversationAgent(agentId);
						const conversationAgentFieldsFromChatNew = conversationAgentFromChatNew
							? { agent: conversationAgentFromChatNew }
							: {};
						dispatch(
							setConversationData({
								conversationId,
								data: {
									sourceAgentId: agentId,
									...conversationAgentFieldsFromChatNew,
								},
							}),
						);
					}
					dispatch(fetchConversation({ conversationId }));
					return;
				}

				// IF chat already exist in conversation
				// switch to that convo -> and it will trigger refetch when ChatHistory is rendered
				if (getState().conversations[conversationId]) {
					const shouldStoreChatNewAgentId =
						!!agentId && isExperimentEnabled('rovo_chat_mention_agents');
					if (shouldStoreChatNewAgentId) {
						const conversationAgentFromChatNew = createProvisionalConversationAgent(agentId);
						const conversationAgentFieldsFromChatNew = conversationAgentFromChatNew
							? { agent: conversationAgentFromChatNew }
							: {};
						dispatch(
							setConversationData({
								conversationId,
								data: {
									sourceAgentId: agentId,
									...conversationAgentFieldsFromChatNew,
								},
							}),
						);
					}
					dispatch(
						setSelectedConversation({
							conversationId,
						}),
					);
					return;
				}

				// IF chat does not exist yet
				// Create conversation optimistically, which will switch to that conversation
				// By switching conversation, it will render ChatHistory
				// ChatHistory will auto fetch the chat data
				// if not found during `sendMessage`, it will auto create one and retry when encountered `404`
				// Create conversation optimistically and store sourceAgentId
				const sourceAgentIdFromChatNew =
					agentId && isExperimentEnabled('rovo_chat_mention_agents') ? agentId : undefined;
				dispatch(
					createConversationOptimistically({
						id: conversationId,
						sourceAgentId: sourceAgentIdFromChatNew,
					}),
				);
			},
	);
